// A record that carries a document but is not a files-list row (an asset's
// document on the employee's Assets tab, a leave request on the Leaves tab),
// in the files list's look: a compact row on a tablet or desktop (like
// file-row.tsx), a card on a phone (like file-card.tsx). No "⋯" menu, no
// selection: the record keeps its own few actions. Slots, top to bottom:
//   pill + title — the status word and the bold name
//   details      — meta lines and notes under the title
//   signers      — chips on the desktop, a list on the phone
//   related      — records nested under this one, shown while `open`
//   primary      — the one highlighted button ("Pasirašyti"), if any
//   actions      — icon buttons on the desktop, labelled buttons on the phone
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import type { LucideIcon } from "lucide-react";
import {
  DISABLED_LINK,
  FOOTER_BUTTON,
  FileTitle,
  ICON_BUTTON,
  SignerChips,
  SignerList,
} from "./file-row-parts";
import type { RowSigner } from "./file-row-model";
import useIsPhone from "./use-is-phone";

// What an action does, named by `kind`: run a handler, open a download in a
// new tab, or go to an in-app page.
type ActionTarget =
  | { kind: "button"; onClick: () => void }
  | { kind: "download"; downloadUrl: string }
  | { kind: "route"; route: string };

export type RecordAction = ActionTarget & {
  key: string;
  // Tooltip of the desktop icon, and the text of the phone button.
  label: string;
  // Shorter phone button text, when `label` does not fit a third of a phone.
  phoneLabel?: string;
  icon: LucideIcon;
  disabled?: boolean;
};

type Props = {
  pill?: ReactNode;
  title: string;
  details?: ReactNode;
  signers: RowSigner[];
  related?: { id: string; open: boolean; rows: ReactNode };
  primary?: { label: string; onClick: () => void } | null;
  actions: RecordAction[];
};

export default function RecordRow({
  pill,
  title,
  details,
  signers,
  related,
  primary,
  actions,
}: Props) {
  const isPhone = useIsPhone();
  // Always in the DOM, so a toggle's aria-controls points at it; the rows
  // mount only while it is open. No display utility: it would beat `hidden`.
  const relatedList = related && (
    <div
      id={related.id}
      hidden={!related.open}
      className={
        isPhone
          ? "mt-1 border-l-2 border-zinc-200 pl-2"
          : "mb-2 ml-3 border-l-2 border-zinc-200 pl-3"
      }
    >
      {related.open && related.rows}
    </div>
  );

  if (isPhone) {
    return (
      <div className="mb-2 flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white p-3 last:mb-0">
        {pill && <div>{pill}</div>}
        <div>
          <p className="line-clamp-3 text-sm leading-snug">
            <FileTitle title={title} url={null} />
          </p>
          {details}
        </div>
        {signers.length > 0 && <SignerList signers={signers} />}
        {relatedList}
        {primary && (
          <button
            type="button"
            className="w-full rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white"
            onClick={primary.onClick}
          >
            {primary.label}
          </button>
        )}
        <div className="flex flex-wrap gap-2 border-t border-zinc-100 pt-2">
          {actions.map((action) => (
            <ActionControl key={action.key} action={action} isPhone />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-zinc-200 last:border-b-0">
      <div className="grid grid-cols-1 items-start gap-3 px-1 py-2.5 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <p className="text-sm leading-snug">
            {pill && <>{pill} </>}
            <FileTitle title={title} url={null} />
          </p>
          {details}
          {signers.length > 0 && <SignerChips signers={signers} />}
        </div>
        <div className="flex flex-wrap items-center gap-1 max-lg:gap-2">
          {primary && (
            <button
              type="button"
              className="mr-1 whitespace-nowrap rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 max-lg:min-h-10 max-lg:px-3 max-lg:text-sm"
              onClick={primary.onClick}
            >
              {primary.label}
            </button>
          )}
          {actions.map((action) => (
            <ActionControl key={action.key} action={action} isPhone={false} />
          ))}
        </div>
      </div>
      {relatedList}
    </div>
  );
}

// Two to a line: a third button of the phone footer wraps instead of being cut off by the card.
const PHONE_FOOTER_BUTTON = clsx(FOOTER_BUTTON, "min-w-[40%]");
// A tablet has no hover for the icon's tooltip, so below `lg` the icon carries its text.
const ICON_BUTTON_WITH_TABLET_TEXT = clsx(ICON_BUTTON, "max-lg:h-10 max-lg:w-auto max-lg:gap-1.5 max-lg:px-3");

function ActionControl({ action, isPhone }: { action: RecordAction; isPhone: boolean }) {
  const Icon = action.icon;
  const content = isPhone ? (
    action.phoneLabel || action.label
  ) : (
    <>
      <Icon className="h-4 w-4" />
      <span className="hidden text-sm font-medium max-lg:inline">{action.phoneLabel || action.label}</span>
    </>
  );
  // The desktop icon gets its name from aria-label; the phone button has text.
  const naming = isPhone ? {} : { "aria-label": action.label, title: action.label };
  const style = isPhone ? PHONE_FOOTER_BUTTON : ICON_BUTTON_WITH_TABLET_TEXT;

  if (action.kind === "button") {
    return (
      <button
        type="button"
        className={clsx(style, "disabled:opacity-40 disabled:hover:bg-white")}
        disabled={action.disabled}
        onClick={action.onClick}
        {...naming}
      >
        {content}
      </button>
    );
  }
  const linkStyle = clsx(style, action.disabled && DISABLED_LINK);
  if (action.kind === "download") {
    return (
      <a
        href={action.disabled ? undefined : action.downloadUrl}
        target="_blank"
        rel="noreferrer"
        className={linkStyle}
        aria-disabled={action.disabled}
        {...naming}
      >
        {content}
      </a>
    );
  }
  return (
    <Link to={action.route} className={linkStyle} aria-disabled={action.disabled} {...naming}>
      {content}
    </Link>
  );
}
